// ============================================================================
// TENANT USER ROLE HOOKS
// ============================================================================
// React hooks for assigning roles to a user within the selected tenant.

import { useState, useEffect, useCallback } from 'react';
import { tenantUserRoleService } from 'api/services';
import type { ID, TenantUserRoleResponse, AssignTenantRoleRequest } from 'api/types';

interface MutationOptions<T = void> {
  onSuccess?: (result: T) => void;
  onError?: (error: Error) => void;
}

/**
 * Roles currently assigned to a user in a tenant.
 * Pass null for either id to skip fetching.
 * @param userId GLOBAL user id
 */
export const useUserRoles = (userId: ID | null, tenantId: ID | null) => {
  const [data, setData] = useState<TenantUserRoleResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [message, setMessage] = useState('');

  const fetchUserRoles = useCallback(async () => {
    if (!userId || !tenantId) {
      setData([]);
      setMessage('');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage('');
    try {
      const response = await tenantUserRoleService.getUserRoles(userId, tenantId);
      if (response.success) {
        setData(response.data ?? []);
      } else {
        setData([]);
        setMessage(response.message || 'Could not load the roles assigned to this user.');
      }
    } catch (err: any) {
      setError(err as Error);
      setData([]);
      setMessage(
        err?.response?.data?.message || 'Could not load the roles assigned to this user.',
      );
    } finally {
      setLoading(false);
    }
  }, [userId, tenantId]);

  useEffect(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  return { data, loading, error, message, refetch: fetchUserRoles };
};

export const useAssignUserRole = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const assignRole = useCallback(
    async (data: AssignTenantRoleRequest, options?: MutationOptions<TenantUserRoleResponse>) => {
      setLoading(true);
      setError(null);
      try {
        const response = await tenantUserRoleService.assign(data);
        if (!response.success) throw new Error(response.message || 'Failed to assign role');
        options?.onSuccess?.(response.data as TenantUserRoleResponse);
        // The whole response is returned, not just data: the API reports the
        // Keycloak outcome in `message` even when the database write succeeded,
        // and the caller must be able to surface a partial sync.
        return response;
      } catch (err) {
        const error = err as Error;
        setError(error);
        options?.onError?.(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { assignRole, loading, error };
};

export const useRevokeUserRole = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const revokeRole = useCallback(
    async (assignmentId: ID, tenantId: ID, options?: MutationOptions) => {
      setLoading(true);
      setError(null);
      try {
        const response = await tenantUserRoleService.revoke(assignmentId, tenantId);
        if (!response.success) throw new Error(response.message || 'Failed to revoke role');
        options?.onSuccess?.();
      } catch (err) {
        const error = err as Error;
        setError(error);
        options?.onError?.(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { revokeRole, loading, error };
};
